import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { BASE_URL } from "../utilities/BASE_URL";
import { fetchWithToken, postWithToken } from "../utilities/fetchWithToken";
import { googleLoginUser, loginUser, logout } from "./authSlice";

// Admin notification blasts, both sides:
//  - user side: the home-screen bell — items with per-user read flags, the unread badge count,
//    and mark-read (fired ONLY when a message is opened in full; the bell/preview never marks).
//  - admin side: isAdmin (shows the hamburger's "Admin controls" entry — cosmetic only, every
//    endpoint re-checks server-side), send, and blast history.
// Live delivery is the ADMIN_BLAST broadcast on /topic/announcements (socketMiddleware), and
// fetchNotifications on every socket (re)connect is the badge's self-heal for missed pushes.

export const fetchAdminStatus = createAsyncThunk(
    "notifications/fetchAdminStatus",
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchWithToken(`${BASE_URL}/admin/status`);
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to fetch admin status");
            }
            const data = await response.json();
            return data.admin === true;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchNotifications = createAsyncThunk(
    "notifications/fetchNotifications",
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const response = await fetchWithToken(`${BASE_URL}/notifications`);
            if (!response.ok) {
                // Backend rejected our token — it's the authority on auth (getPlayerChips pattern).
                if (response.status === 401) {
                    dispatch(logout());
                }
                const text = await response.text();
                return rejectWithValue(text || "Failed to fetch notifications");
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const markNotificationRead = createAsyncThunk(
    "notifications/markRead",
    async (id, { rejectWithValue }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/notifications/${id}/read`);
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to mark notification read");
            }
            const data = await response.json();
            return { id, unreadCount: data.unreadCount };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    },
    {
        // Duplicate guard: opening an already-read message re-posts nothing (server is
        // idempotent anyway — this just saves the round trip).
        condition: (id, { getState }) => {
            const item = getState().notifications.items.find((n) => n.id === id);
            return !item || !item.read;
        },
    }
);

export const sendBlast = createAsyncThunk(
    "notifications/sendBlast",
    async ({ subject, body }, { rejectWithValue }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/admin/blasts`, { subject, body });
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to send blast");
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchBlastHistory = createAsyncThunk(
    "notifications/fetchBlastHistory",
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchWithToken(`${BASE_URL}/admin/blasts`);
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to fetch blast history");
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const initialState = {
    isAdmin: false,
    items: [],          // [{id, subject, body, createdAt, read}] newest first
    unreadCount: 0,
    sendStatus: "idle", // idle | pending | sent | failed
    sendError: null,
    history: [],        // [{id, subject, body, senderUsername, createdAt, readCount}]
    historyLoading: false,
};

const notificationsSlice = createSlice({
    name: "notifications",
    initialState,
    reducers: {
        // A live ADMIN_BLAST push from /topic/announcements. Dedupe by id — the push can race
        // the reconnect refetch.
        blastReceived: (state, action) => {
            const blast = action.payload;
            if (!blast || blast.id == null) return;
            if (state.items.some((n) => n.id === blast.id)) return;
            state.items.unshift({ ...blast, read: false });
            state.unreadCount += 1;
        },
        resetSendStatus: (state) => {
            state.sendStatus = "idle";
            state.sendError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAdminStatus.fulfilled, (state, action) => {
                state.isAdmin = action.payload;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                // Authoritative replace (server owns read state and ordering).
                state.items = action.payload.notifications || [];
                state.unreadCount = action.payload.unreadCount || 0;
            })
            .addCase(markNotificationRead.fulfilled, (state, action) => {
                const item = state.items.find((n) => n.id === action.payload.id);
                if (item) item.read = true;
                state.unreadCount = action.payload.unreadCount ?? state.unreadCount;
            })
            .addCase(sendBlast.pending, (state) => {
                state.sendStatus = "pending";
                state.sendError = null;
            })
            .addCase(sendBlast.fulfilled, (state) => {
                state.sendStatus = "sent";
            })
            .addCase(sendBlast.rejected, (state, action) => {
                state.sendStatus = "failed";
                state.sendError = action.payload || "Failed to send blast";
            })
            .addCase(fetchBlastHistory.pending, (state) => {
                state.historyLoading = true;
            })
            .addCase(fetchBlastHistory.fulfilled, (state, action) => {
                state.historyLoading = false;
                state.history = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchBlastHistory.rejected, (state) => {
                state.historyLoading = false;
            })
            // Full reset on logout so the badge, items and isAdmin never leak across accounts.
            .addCase(loginUser.fulfilled, () => initialState)
            .addCase(googleLoginUser.fulfilled, () => initialState)
            .addCase(logout, () => initialState);
    },
});

export const { blastReceived, resetSendStatus } = notificationsSlice.actions;
export default notificationsSlice.reducer;
